import { supabase } from '../lib/supabase';

// ============================================================================
// UTILITY FUNCTIONS - Case Conversion
// ============================================================================

const toCamelCase = (str) => {
  return str?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
};

const toSnakeCase = (str) => {
  return str?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
};

const convertKeysToCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => convertKeysToCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = toCamelCase(key);
      acc[camelKey] = convertKeysToCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

const convertKeysToSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => convertKeysToSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = toSnakeCase(key);
      acc[snakeKey] = convertKeysToSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

const isSchemaError = (error) => {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
};

// ============================================================================
// ROLES SERVICE
// ============================================================================

export const rolesService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('roles')?.select('*')?.order('name', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAll roles:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getAll roles:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  },

  async create(roleData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(roleData);
      const { data, error } = await supabase?.from('roles')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in create role:', error?.message);
          throw error;
        } else {
          console.log('Data error in create role:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  },

  async update(id, roleData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(roleData);
      const { data, error } = await supabase?.from('roles')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in update role:', error?.message);
          throw error;
        } else {
          console.log('Data error in update role:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('roles')?.delete()?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in delete role:', error?.message);
          throw error;
        } else {
          console.log('Data error in delete role:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  }
};

// ============================================================================
// DEPARTMENTS SERVICE
// ============================================================================

export const departmentsService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('departments')?.select('*')?.order('name', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAll departments:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getAll departments:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw error;
    }
  },

  async create(deptData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(deptData);
      const { data, error } = await supabase?.from('departments')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in create department:', error?.message);
          throw error;
        } else {
          console.log('Data error in create department:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error creating department:', error);
      throw error;
    }
  },

  async update(id, deptData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(deptData);
      const { data, error } = await supabase?.from('departments')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in update department:', error?.message);
          throw error;
        } else {
          console.log('Data error in update department:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error updating department:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('departments')?.delete()?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in delete department:', error?.message);
          throw error;
        } else {
          console.log('Data error in delete department:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting department:', error);
      throw error;
    }
  }
};

// ============================================================================
// JOB FIELDS SERVICE
// ============================================================================

export const jobFieldsService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('job_fields')?.select('*')?.order('field_name', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAll job_fields:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getAll job_fields:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching job fields:', error);
      throw error;
    }
  },

  async create(fieldData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(fieldData);
      const { data, error } = await supabase?.from('job_fields')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in create job_field:', error?.message);
          throw error;
        } else {
          console.log('Data error in create job_field:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error creating job field:', error);
      throw error;
    }
  },

  async update(id, fieldData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(fieldData);
      const { data, error } = await supabase?.from('job_fields')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in update job_field:', error?.message);
          throw error;
        } else {
          console.log('Data error in update job_field:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error updating job field:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('job_fields')?.delete()?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in delete job_field:', error?.message);
          throw error;
        } else {
          console.log('Data error in delete job_field:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting job field:', error);
      throw error;
    }
  }
};

// ============================================================================
// VIOLATION CATEGORIES SERVICE
// ============================================================================

export const violationCategoriesService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('violation_categories')?.select('*')?.order('category', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAll violation_categories:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getAll violation_categories:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching violation categories:', error);
      throw error;
    }
  },

  async create(categoryData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(categoryData);
      const { data, error } = await supabase?.from('violation_categories')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in create violation_category:', error?.message);
          throw error;
        } else {
          console.log('Data error in create violation_category:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error creating violation category:', error);
      throw error;
    }
  },

  async update(id, categoryData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(categoryData);
      const { data, error } = await supabase?.from('violation_categories')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in update violation_category:', error?.message);
          throw error;
        } else {
          console.log('Data error in update violation_category:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error updating violation category:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('violation_categories')?.delete()?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in delete violation_category:', error?.message);
          throw error;
        } else {
          console.log('Data error in delete violation_category:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting violation category:', error);
      throw error;
    }
  }
};

// ============================================================================
// POINT THRESHOLDS SERVICE
// ============================================================================

export const pointThresholdsService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('point_thresholds')?.select('*')?.order('points', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAll point_thresholds:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getAll point_thresholds:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching point thresholds:', error);
      throw error;
    }
  },

  async update(id, thresholdData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(thresholdData);
      const { data, error } = await supabase?.from('point_thresholds')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in update point_threshold:', error?.message);
          throw error;
        } else {
          console.log('Data error in update point_threshold:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error updating point threshold:', error);
      throw error;
    }
  }
};

// ============================================================================
// COMPLIANCE RULES SERVICE
// ============================================================================

export const complianceRulesService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('compliance_rules')?.select('*')?.order('priority', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAll compliance_rules:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getAll compliance_rules:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching compliance rules:', error);
      throw error;
    }
  },

  async create(ruleData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(ruleData);
      const { data, error } = await supabase?.from('compliance_rules')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in create compliance_rule:', error?.message);
          throw error;
        } else {
          console.log('Data error in create compliance_rule:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error creating compliance rule:', error);
      throw error;
    }
  },

  async update(id, ruleData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(ruleData);
      const { data, error } = await supabase?.from('compliance_rules')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in update compliance_rule:', error?.message);
          throw error;
        } else {
          console.log('Data error in update compliance_rule:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error updating compliance rule:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('compliance_rules')?.delete()?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in delete compliance_rule:', error?.message);
          throw error;
        } else {
          console.log('Data error in delete compliance_rule:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting compliance rule:', error);
      throw error;
    }
  }
};

// ============================================================================
// EMPLOYEES SERVICE
// ============================================================================

export const employeesService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('employees')?.select('*')?.order('name', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAll employees:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getAll employees:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('employees')?.select('*')?.eq('id', id)?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getById employee:', error?.message);
          throw error;
        } else {
          console.log('Data error in getById employee:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error fetching employee:', error);
      throw error;
    }
  },

  async create(employeeData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(employeeData);
      const { data, error } = await supabase?.from('employees')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in create employee:', error?.message);
          throw error;
        } else {
          console.log('Data error in create employee:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error creating employee:', error);
      throw error;
    }
  },

  async update(id, employeeData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(employeeData);
      const { data, error } = await supabase?.from('employees')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in update employee:', error?.message);
          throw error;
        } else {
          console.log('Data error in update employee:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('employees')?.delete()?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in delete employee:', error?.message);
          throw error;
        } else {
          console.log('Data error in delete employee:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting employee:', error);
      throw error;
    }
  },

  async bulkCreate(employeesData) {
    try {
      // Generate unique placeholder emails for duplicates within the batch
      const emailCounts = {};
      const processedEmployees = employeesData?.map(emp => {
        const email = emp?.email?.toLowerCase()?.trim();
        
        // Check if this is a placeholder email (common patterns)
        const isPlaceholder = email?.includes('placeholder') || 
                             email?.includes('temp') || 
                             email?.includes('pending') ||
                             email === 'noemail@example.com' ||
                             email === 'unknown@example.com';
        
        if (isPlaceholder) {
          // Track how many times we've seen this placeholder
          emailCounts[email] = (emailCounts?.[email] || 0) + 1;
          
          // If this is a duplicate placeholder, generate unique one
          if (emailCounts?.[email] > 1) {
            const timestamp = Date.now();
            const randomSuffix = Math.random()?.toString(36)?.substring(2, 8);
            return {
              ...emp,
              email: `placeholder_${timestamp}_${randomSuffix}@example.com`
            };
          }
        }
        
        return emp;
      });

      // First, check for duplicate emails in the database
      const emailsToCheck = processedEmployees?.map(emp => emp?.email)?.filter(Boolean);
      
      if (emailsToCheck?.length === 0) {
        throw new Error('No valid employee data to import');
      }

      // Query existing emails
      const { data: existingEmployees, error: checkError } = await supabase
        ?.from('employees')
        ?.select('email')
        ?.in('email', emailsToCheck);

      if (checkError && !isSchemaError(checkError)) {
        console.error('Error checking existing emails:', checkError);
      }

      const existingEmails = new Set(existingEmployees?.map(emp => emp?.email?.toLowerCase()) || []);
      
      // Filter out employees with duplicate emails
      const newEmployees = [];
      const duplicates = [];

      processedEmployees?.forEach(emp => {
        if (existingEmails?.has(emp?.email?.toLowerCase())) {
          duplicates?.push(emp?.email);
        } else {
          newEmployees?.push(emp);
        }
      });

      // If all employees are duplicates, throw error
      if (newEmployees?.length === 0) {
        throw new Error(`All ${duplicates?.length} employees already exist in the database. Duplicate emails: ${duplicates?.slice(0, 5)?.join(', ')}${duplicates?.length > 5 ? '...' : ''}`);
      }

      // Generate unique employee_id for each new employee
      // Query existing employee_id values to find the maximum number
      const { data: existingEmployeeIds, error: idError } = await supabase
        ?.from('employees')
        ?.select('employee_id')
        ?.order('employee_id', { ascending: false })
        ?.limit(1);

      let startingNumber = 1;
      
      if (!idError && existingEmployeeIds && existingEmployeeIds?.length > 0) {
        // Extract the numeric part from the last employee_id (format: EMP-2024-XXX)
        const lastEmployeeId = existingEmployeeIds?.[0]?.employee_id;
        const match = lastEmployeeId?.match(/EMP-\d{4}-(\d+)/);
        if (match) {
          startingNumber = parseInt(match?.[1], 10) + 1;
        }
      }
      
      const employeesWithIds = newEmployees?.map((emp, index) => ({
        ...emp,
        employeeId: `EMP-2024-${String(startingNumber + index)?.padStart(3, '0')}`,
        complianceStatus: emp?.complianceStatus || 'Good',
        avatar: emp?.avatar || "https://img.rocket.new/generatedImages/rocket_gen_img_152474a04-1767640485316.png",
        avatarAlt: emp?.avatarAlt || `Profile picture of ${emp?.name}`,
        currentPoints: emp?.currentPoints || 0,
        complianceScore: emp?.complianceScore || 100,
        recentViolations: emp?.recentViolations || 0,
        trend: emp?.trend || 'stable'
      }));

      // Insert only new employees
      const snakeCaseData = employeesWithIds?.map(emp => convertKeysToSnakeCase(emp));
      const { data, error } = await supabase?.from('employees')?.insert(snakeCaseData)?.select();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in bulkCreate employees:', error?.message);
          throw error;
        } else {
          console.log('Data error in bulkCreate employees:', error?.message);
          throw new Error(`Failed to import employees: ${error?.message || 'Database validation error'}`);
        }
      }

      // Return result with duplicate info
      const result = {
        imported: convertKeysToCamelCase(data || []),
        duplicates: duplicates,
        duplicateCount: duplicates?.length,
        importedCount: data?.length || 0
      };

      return result;
    } catch (error) {
      console.error('Error bulk creating employees:', error);
      throw error;
    }
  }
};

// ============================================================================
// NOTIFICATIONS SERVICE
// ============================================================================

export const notificationsService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAll notifications:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getAll notifications:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  async getUnreadCount() {
    try {
      const { count, error } = await supabase?.from('notifications')?.select('*', { count: 'exact', head: true })?.eq('is_read', false);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getUnreadCount notifications:', error?.message);
          throw error;
        } else {
          console.log('Data error in getUnreadCount notifications:', error?.message);
          return 0;
        }
      }
      
      return count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  },

  async getUnread() {
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('is_read', false)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getUnread notifications:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getUnread notifications:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching unread notifications:', error);
      return [];
    }
  },

  async markAsRead(id) {
    try {
      const { data, error } = await supabase?.from('notifications')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in markAsRead notification:', error?.message);
          throw error;
        } else {
          console.log('Data error in markAsRead notification:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return null;
    }
  },

  async markAllAsRead() {
    try {
      const { error } = await supabase?.from('notifications')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.eq('is_read', false);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in markAllAsRead notifications:', error?.message);
          throw error;
        } else {
          console.log('Data error in markAllAsRead notifications:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  },

  async create(notificationData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(notificationData);
      const { data, error } = await supabase?.from('notifications')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in create notification:', error?.message);
          throw error;
        } else {
          console.log('Data error in create notification:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error creating notification:', error);
      return null;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('notifications')?.delete()?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in delete notification:', error?.message);
          throw error;
        } else {
          console.log('Data error in delete notification:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  },

  // ============================================================================
  // DELIVERY STATUS TRACKING
  // ============================================================================

  async markAsDelivered(notificationId) {
    try {
      const { data, error } = await supabase?.rpc('mark_notification_delivered', {
        notification_id: notificationId
      });

      if (error) {
        console.error('Error marking notification as delivered:', error?.message);
        return false;
      }

      return data;
    } catch (error) {
      console.error('Error marking notification as delivered:', error);
      return false;
    }
  },

  async markAsFailed(notificationId, errorMessage = null) {
    try {
      const { data, error } = await supabase?.rpc('mark_notification_failed', {
        notification_id: notificationId,
        error_message: errorMessage
      });

      if (error) {
        console.error('Error marking notification as failed:', error?.message);
        return false;
      }

      return data;
    } catch (error) {
      console.error('Error marking notification as failed:', error);
      return false;
    }
  },

  async getNotificationsForRetry() {
    try {
      const { data, error } = await supabase?.rpc('get_notifications_for_retry');

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getNotificationsForRetry:', error?.message);
          throw error;
        } else {
          console.log('Data error in getNotificationsForRetry:', error?.message);
          return [];
        }
      }

      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching notifications for retry:', error);
      return [];
    }
  },

  async getPendingNotifications() {
    try {
      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('delivery_status', 'pending')
        ?.order('created_at', { ascending: true });

      if (error) {
        // Silently handle schema cache issues - column exists but PostgREST cache may not be updated
        if (error?.message?.includes('does not exist')) {
          return [];
        }
        if (isSchemaError(error)) {
          console.error('Schema error in getPendingNotifications:', error?.message);
          throw error;
        } else {
          console.log('Data error in getPendingNotifications:', error?.message);
          return [];
        }
      }

      return convertKeysToCamelCase(data || []);
    } catch (error) {
      // Silently handle column not found errors during schema cache refresh
      if (error?.message?.includes('does not exist')) {
        return [];
      }
      console.error('Error fetching pending notifications:', error);
      return [];
    }
  },

  async getDeliveryStats() {
    try {
      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('delivery_status');

      if (error) {
        // Silently handle schema cache issues - column exists but PostgREST cache may not be updated
        if (error?.message?.includes('does not exist')) {
          // Return default stats without logging - this is a temporary cache issue
          return { pending: 0, delivered: 0, failed: 0, total: 0 };
        }
        console.error('Error fetching delivery stats:', error?.message);
        return { pending: 0, delivered: 0, failed: 0, total: 0 };
      }

      const stats = {
        pending: 0,
        delivered: 0,
        failed: 0,
        total: data?.length || 0
      };

      data?.forEach(notification => {
        const status = notification?.delivery_status;
        if (status && stats?.hasOwnProperty(status)) {
          stats[status]++;
        }
      });

      return stats;
    } catch (error) {
      // Silently handle any errors - delivery stats are non-critical
      return { pending: 0, delivered: 0, failed: 0, total: 0 };
    }
  },

  // ============================================================================
  // RETRY LOGIC
  // ============================================================================

  async processRetries() {
    try {
      const notificationsToRetry = await this.getNotificationsForRetry();

      if (notificationsToRetry?.length === 0) {
        return { processed: 0, succeeded: 0, failed: 0 };
      }

      let succeeded = 0;
      let failed = 0;

      for (const notification of notificationsToRetry) {
        try {
          // Simulate delivery attempt (in real app, this would be actual delivery logic)
          const deliverySuccess = await this.attemptDelivery(notification);

          if (deliverySuccess) {
            await this.markAsDelivered(notification?.id);
            succeeded++;
          } else {
            await this.markAsFailed(notification?.id, 'Delivery attempt failed');
            failed++;
          }
        } catch (error) {
          await this.markAsFailed(notification?.id, error?.message);
          failed++;
        }
      }

      return {
        processed: notificationsToRetry?.length,
        succeeded,
        failed
      };
    } catch (error) {
      console.error('Error processing notification retries:', error);
      return { processed: 0, succeeded: 0, failed: 0 };
    }
  },

  async attemptDelivery(notification) {
    try {
      // Simulate delivery logic - in production this would be:
      // - Push notification to device
      // - Email notification
      // - SMS notification
      // - Webhook call
      // For now, we'll mark as delivered immediately
      return true;
    } catch (error) {
      console.error('Delivery attempt failed:', error);
      return false;
    }
  },

  // ============================================================================
  // EVENT-SPECIFIC NOTIFICATION CREATION
  // ============================================================================

  async createViolationNotification(violationData) {
    try {
      const { data, error } = await supabase?.rpc('create_violation_notification', {
        p_employee_id: violationData?.employeeId,
        p_employee_name: violationData?.employeeName,
        p_employee_department: violationData?.employeeDepartment,
        p_violation_id: violationData?.violationId,
        p_violation_type: violationData?.violationType,
        p_violation_points: violationData?.violationPoints,
        p_current_points: violationData?.currentPoints,
        p_compliance_score: violationData?.complianceScore,
        p_compliance_status: violationData?.complianceStatus
      });

      if (error) {
        console.error('Error creating violation notification:', error?.message);
        return null;
      }

      // Attempt immediate delivery
      if (data) {
        await this.attemptDelivery({ id: data });
        await this.markAsDelivered(data);
      }

      return data;
    } catch (error) {
      console.error('Error creating violation notification:', error);
      return null;
    }
  },

  async createViolationEditNotification(editData) {
    try {
      const { data, error } = await supabase?.rpc('create_violation_edit_notification', {
        p_employee_id: editData?.employeeId,
        p_employee_name: editData?.employeeName,
        p_employee_department: editData?.employeeDepartment,
        p_violation_id: editData?.violationId,
        p_old_points: editData?.oldPoints,
        p_new_points: editData?.newPoints,
        p_current_points: editData?.currentPoints,
        p_compliance_score: editData?.complianceScore,
        p_compliance_status: editData?.complianceStatus
      });

      if (error) {
        console.error('Error creating violation edit notification:', error?.message);
        return null;
      }

      // Attempt immediate delivery
      if (data) {
        await this.attemptDelivery({ id: data });
        await this.markAsDelivered(data);
      }

      return data;
    } catch (error) {
      console.error('Error creating violation edit notification:', error);
      return null;
    }
  },

  async createPointCreditNotification(creditData) {
    try {
      const { data, error } = await supabase?.rpc('create_point_credit_notification', {
        p_employee_id: creditData?.employeeId,
        p_employee_name: creditData?.employeeName,
        p_employee_department: creditData?.employeeDepartment,
        p_points_credited: creditData?.pointsCredited,
        p_points_before: creditData?.pointsBefore,
        p_points_after: creditData?.pointsAfter,
        p_days_without_violation: creditData?.daysWithoutViolation,
        p_compliance_score: creditData?.complianceScore,
        p_compliance_status: creditData?.complianceStatus
      });

      if (error) {
        console.error('Error creating point credit notification:', error?.message);
        return null;
      }

      // Attempt immediate delivery
      if (data) {
        await this.attemptDelivery({ id: data });
        await this.markAsDelivered(data);
      }

      return data;
    } catch (error) {
      console.error('Error creating point credit notification:', error);
      return null;
    }
  },

  async checkThresholdAndNotify(employeeId, employeeName, employeeDepartment, currentPoints, complianceScore, complianceStatus) {
    try {
      // Fetch point thresholds to determine if notification needed
      const { data: thresholds, error: thresholdError } = await supabase?.from('point_thresholds')?.select('*')?.order('points', { ascending: true });
      
      if (thresholdError || !thresholds || thresholds?.length === 0) {
        return null;
      }

      // Find the threshold that was just exceeded
      const exceededThreshold = thresholds?.find(t => parseFloat(currentPoints) >= t?.points);
      
      if (!exceededThreshold) {
        return null;
      }

      // Determine priority based on threshold color
      const priorityMap = {
        'success': 'low',
        'warning': 'medium',
        'error': 'high',
        'destructive': 'critical'
      };

      const priority = priorityMap?.[exceededThreshold?.color] || 'medium';

      // Create notification for threshold exceeded
      const notification = {
        employeeId,
        type: 'threshold_exceeded',
        priority,
        title: `${exceededThreshold?.name} Threshold Exceeded`,
        message: `${employeeName} has exceeded the ${exceededThreshold?.name} threshold with ${currentPoints} violation points. Action required: ${exceededThreshold?.action}`,
        thresholdName: exceededThreshold?.name,
        thresholdPoints: exceededThreshold?.points,
        employeeName,
        employeeDepartment,
        currentPoints: parseFloat(currentPoints),
        complianceScore,
        complianceStatus,
        isRead: false,
        deliveryStatus: 'pending',
        eventType: 'threshold_exceeded'
      };

      const createdNotification = await this.create(notification);

      // Attempt immediate delivery
      if (createdNotification?.id) {
        const deliverySuccess = await this.attemptDelivery(createdNotification);
        if (deliverySuccess) {
          await this.markAsDelivered(createdNotification?.id);
        } else {
          await this.markAsFailed(createdNotification?.id, 'Initial delivery failed');
        }
      }

      // Also create notification if compliance score drops below 70
      if (complianceScore < 70) {
        const scoreNotification = {
          employeeId,
          type: 'compliance_drop',
          priority: complianceScore < 50 ? 'critical' : 'high',
          title: 'Compliance Score Drop Alert',
          message: `${employeeName} compliance score has dropped to ${complianceScore}%. Immediate review required.`,
          employeeName,
          employeeDepartment,
          currentPoints: parseFloat(currentPoints),
          complianceScore,
          complianceStatus,
          isRead: false,
          deliveryStatus: 'pending',
          eventType: 'compliance_drop'
        };
        const scoreNotif = await this.create(scoreNotification);
        if (scoreNotif?.id) {
          const deliverySuccess = await this.attemptDelivery(scoreNotif);
          if (deliverySuccess) {
            await this.markAsDelivered(scoreNotif?.id);
          }
        }
      }

      // Create critical status notification if status is Critical
      if (complianceStatus === 'Critical') {
        const criticalNotification = {
          employeeId,
          type: 'critical_status',
          priority: 'critical',
          title: 'Critical Compliance Status',
          message: `${employeeName} has reached Critical compliance status with ${currentPoints} points. Immediate intervention required.`,
          employeeName,
          employeeDepartment,
          currentPoints: parseFloat(currentPoints),
          complianceScore,
          complianceStatus,
          isRead: false,
          deliveryStatus: 'pending',
          eventType: 'critical_status'
        };
        const criticalNotif = await this.create(criticalNotification);
        if (criticalNotif?.id) {
          const deliverySuccess = await this.attemptDelivery(criticalNotif);
          if (deliverySuccess) {
            await this.markAsDelivered(criticalNotif?.id);
          }
        }
      }

      return createdNotification;
    } catch (error) {
      console.error('Error checking threshold and creating notification:', error);
      return null;
    }
  },

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToNotifications(callback) {
    const subscription = supabase
      ?.channel('notifications_channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.subscribe();

    return subscription;
  },

  subscribeToDeliveryStatus(callback) {
    const subscription = supabase
      ?.channel('notification_delivery_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: 'delivery_status=neq.pending'
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.subscribe();

    return subscription;
  },

  subscribeToViolationEvents(callback) {
    const subscription = supabase
      ?.channel('violation_events_channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'violation_records'
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.subscribe();

    return subscription;
  },

  unsubscribe(subscription) {
    if (subscription) {
      supabase?.removeChannel(subscription);
    }
  },

  // ============================================================================
  // CLEANUP
  // ============================================================================

  async cleanupOldNotifications() {
    try {
      const { data, error } = await supabase?.rpc('cleanup_old_notifications');

      if (error) {
        console.error('Error cleaning up old notifications:', error?.message);
        return 0;
      }

      return data || 0;
    } catch (error) {
      console.error('Error cleaning up old notifications:', error);
      return 0;
    }
  }
};

// ============================================================================
// VIOLATION RECORDS SERVICE
// ============================================================================

export const violationRecordsService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('violation_records')?.select('*')?.order('date', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAll violation_records:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getAll violation_records:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching violation records:', error);
      throw error;
    }
  },

  async getByEmployeeId(employeeId) {
    try {
      const { data, error } = await supabase?.from('violation_records')?.select('*')?.eq('employee_id', employeeId)?.order('date', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getByEmployeeId violation_records:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getByEmployeeId violation_records:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching violation records:', error);
      throw error;
    }
  },

  async create(violationData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(violationData);
      const { data, error } = await supabase?.from('violation_records')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in create violation_record:', error?.message);
          throw error;
        } else {
          console.log('Data error in create violation_record:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error creating violation record:', error);
      throw error;
    }
  },

  async update(id, violationData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase(violationData);
      const { data, error } = await supabase?.from('violation_records')?.update(snakeCaseData)?.eq('id', id)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in update violation_record:', error?.message);
          throw error;
        } else {
          console.log('Data error in update violation_record:', error?.message);
          return null;
        }
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Error updating violation record:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('violation_records')?.delete()?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in delete violation_record:', error?.message);
          throw error;
        } else {
          console.log('Data error in delete violation_record:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting violation record:', error);
      throw error;
    }
  },

  async createWithEmployeeUpdate(violationData) {
    try {
      // Remove fields that don't exist in violation_records table schema
      const { severity, status, ...validViolationData } = violationData;
      
      // Step 1: Create the violation record
      const snakeCaseData = convertKeysToSnakeCase(validViolationData);
      const { data: violationRecord, error: violationError } = await supabase
        ?.from('violation_records')
        ?.insert(snakeCaseData)
        ?.select()
        ?.single();
      
      if (violationError) {
        if (isSchemaError(violationError)) {
          console.error('Schema error in create violation_record:', violationError?.message);
          throw violationError;
        } else {
          console.log('Data error in create violation_record:', violationError?.message);
          return null;
        }
      }

      // Step 2: Get current employee data (including name and department)
      const { data: employee, error: employeeError } = await supabase
        ?.from('employees')?.select('current_points, recent_violations, name, department')?.eq('id', validViolationData?.employeeId)
        ?.single();

      if (employeeError) {
        console.error('Error fetching employee for points update:', employeeError?.message);
        // Return violation even if employee update fails
        return convertKeysToCamelCase(violationRecord);
      }

      // Step 3: Calculate new points and compliance status
      const currentPoints = parseFloat(employee?.current_points || 0);
      const violationPoints = parseFloat(validViolationData?.points || 0);
      const newTotalPoints = currentPoints + violationPoints;
      const recentViolations = (employee?.recent_violations || 0) + 1;

      // Determine compliance status based on points
      let complianceStatus = 'Good';
      if (newTotalPoints >= 20) {
        complianceStatus = 'Critical';
      } else if (newTotalPoints >= 10) {
        complianceStatus = 'Warning';
      } else if (newTotalPoints >= 5) {
        complianceStatus = 'Good';
      } else {
        complianceStatus = 'Excellent';
      }

      // Calculate compliance score (100 - points, minimum 0)
      const complianceScore = Math.max(0, 100 - Math.floor(newTotalPoints));

      // Step 4: Update employee with new points and compliance data
      const { error: updateError } = await supabase
        ?.from('employees')
        ?.update({
          current_points: newTotalPoints,
          recent_violations: recentViolations,
          compliance_status: complianceStatus,
          compliance_score: complianceScore
        })
        ?.eq('id', validViolationData?.employeeId);

      if (updateError) {
        console.error('Error updating employee points:', updateError?.message);
      }

      // Step 5: Check thresholds and notify
      await notificationsService?.checkThresholdAndNotify(
        validViolationData?.employeeId,
        employee?.name || 'Unknown Employee',employee?.department || 'Unknown Department',
        newTotalPoints,
        complianceScore,
        complianceStatus
      );

      return convertKeysToCamelCase(violationRecord);
    } catch (error) {
      console.error('Error creating violation with employee update:', error);
      throw error;
    }
  },

  async updateWithEmployeeRecalculation(id, violationData) {
    try {
      // Step 1: Get the original violation record to calculate point difference
      const { data: originalViolation, error: fetchError } = await supabase
        ?.from('violation_records')?.select('*')?.eq('id', id)
        ?.single();

      if (fetchError || !originalViolation) {
        console.error('Error fetching original violation:', fetchError?.message);
        return null;
      }

      // Step 2: Store original points if not already stored
      const originalPoints = originalViolation?.original_points || originalViolation?.points;
      const oldPoints = parseFloat(originalViolation?.points || 0);
      const newPoints = parseFloat(violationData?.points || 0);
      const pointsDifference = newPoints - oldPoints;

      // Step 3: Build edit history entry
      const editHistory = originalViolation?.edit_history || [];
      const editEntry = {
        editedAt: new Date()?.toISOString(),
        editedBy: violationData?.editedBy || 'System',
        changes: {
          type: { old: originalViolation?.type, new: violationData?.type },
          category: { old: originalViolation?.category, new: violationData?.category },
          points: { old: oldPoints, new: newPoints },
          date: { old: originalViolation?.date, new: violationData?.date },
          description: { old: originalViolation?.description, new: violationData?.description }
        }
      };
      editHistory?.push(editEntry);

      // Step 4: Update the violation record with edit tracking
      const updateData = {
        ...violationData,
        originalPoints: originalPoints,
        editedAt: new Date()?.toISOString(),
        editedBy: violationData?.editedBy || 'System',
        editHistory: editHistory
      };

      const snakeCaseData = convertKeysToSnakeCase(updateData);
      const { data: updatedViolation, error: updateError } = await supabase
        ?.from('violation_records')
        ?.update(snakeCaseData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (updateError) {
        if (isSchemaError(updateError)) {
          console.error('Schema error in update violation_record:', updateError?.message);
          throw updateError;
        } else {
          console.log('Data error in update violation_record:', updateError?.message);
          return null;
        }
      }

      // Step 5: Update employee points if points changed
      if (pointsDifference !== 0) {
        const { data: employee, error: employeeError } = await supabase
          ?.from('employees')
          ?.select('current_points, compliance_score, name, department')
          ?.eq('id', originalViolation?.employee_id)
          ?.single();

        if (employeeError) {
          console.error('Error fetching employee for recalculation:', employeeError?.message);
          return convertKeysToCamelCase(updatedViolation);
        }

        const currentPoints = parseFloat(employee?.current_points || 0);
        const newTotalPoints = Math.max(0, currentPoints + pointsDifference);

        // Recalculate compliance status
        let complianceStatus = 'Good';
        if (newTotalPoints >= 20) {
          complianceStatus = 'Critical';
        } else if (newTotalPoints >= 10) {
          complianceStatus = 'Warning';
        } else if (newTotalPoints >= 5) {
          complianceStatus = 'Good';
        } else {
          complianceStatus = 'Excellent';
        }

        const complianceScore = Math.max(0, 100 - Math.floor(newTotalPoints));

        // Update employee
        const { error: empUpdateError } = await supabase
          ?.from('employees')
          ?.update({
            current_points: newTotalPoints,
            compliance_status: complianceStatus,
            compliance_score: complianceScore
          })
          ?.eq('id', originalViolation?.employee_id);

        if (empUpdateError) {
          console.error('Error updating employee points:', empUpdateError?.message);
        }

        // Check thresholds and notify
        await notificationsService?.checkThresholdAndNotify(
          originalViolation?.employee_id,
          employee?.name || 'Unknown Employee',
          employee?.department || 'Unknown Department',
          newTotalPoints,
          complianceScore,
          complianceStatus
        );
      }

      return convertKeysToCamelCase(updatedViolation);
    } catch (error) {
      console.error('Error updating violation with employee recalculation:', error);
      throw error;
    }
  },

  async applyAutomaticPointCredits() {
    try {
      // Call the database function to apply credits
      const { data, error } = await supabase?.rpc('apply_automatic_point_credits');

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in apply_automatic_point_credits:', error?.message);
          throw error;
        } else {
          console.log('Data error in apply_automatic_point_credits:', error?.message);
          return [];
        }
      }

      // Log the credits to point_credit_log table
      if (data && data?.length > 0) {
        const creditLogs = data?.map(credit => ({
          employeeId: credit?.employee_id,
          employeeName: credit?.employee_name,
          pointsCredited: 1.0,
          pointsBefore: credit?.points_before,
          pointsAfter: credit?.points_after,
          daysWithoutViolation: credit?.days_without_violation,
          reason: 'Automatic 30-day credit'
        }));

        const snakeCaseLogs = creditLogs?.map(log => convertKeysToSnakeCase(log));
        const { error: logError } = await supabase
          ?.from('point_credit_log')
          ?.insert(snakeCaseLogs);

        if (logError && !isSchemaError(logError)) {
          console.error('Error logging point credits:', logError?.message);
        }
      }

      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error applying automatic point credits:', error);
      throw error;
    }
  },

  async getPointCreditLog(employeeId = null) {
    try {
      let query = supabase?.from('point_credit_log')?.select('*')?.order('credited_at', { ascending: false });

      if (employeeId) {
        query = query?.eq('employee_id', employeeId);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getPointCreditLog:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getPointCreditLog:', error?.message);
          return [];
        }
      }

      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching point credit log:', error);
      return [];
    }
  }
};

// ============================================================================
// DATA RESET SERVICE
// ============================================================================

export const dataResetService = {
  async resetAllData() {
    try {
      // Delete in correct order due to foreign key constraints
      // 1. Delete violation records first (has FK to employees)
      const { error: violationsError } = await supabase
        ?.from('violation_records')
        ?.delete()
        ?.neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all records
      
      if (violationsError && !isSchemaError(violationsError)) {
        console.error('Error deleting violation records:', violationsError?.message);
      }

      // 2. Delete all employees
      const { error: employeesError } = await supabase
        ?.from('employees')
        ?.delete()
        ?.neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all records
      
      if (employeesError && !isSchemaError(employeesError)) {
        console.error('Error deleting employees:', employeesError?.message);
      }

      // Check if there were any schema errors
      if (isSchemaError(violationsError) || isSchemaError(employeesError)) {
        throw new Error('Schema error occurred during data reset');
      }

      return {
        success: true,
        message: 'All employee and violation data has been cleared successfully'
      };
    } catch (error) {
      console.error('Error resetting data:', error);
      throw error;
    }
  }
};

// ============================================================================
// AUDIT LOGS SERVICE
// ============================================================================

export const auditLogsService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('audit_logs')?.select('*')?.order('timestamp', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getAll audit_logs:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getAll audit_logs:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  },

  async getFiltered({ actionType, entityType, startDate, endDate, searchTerm, limit = 100 }) {
    try {
      let query = supabase?.from('audit_logs')?.select('*', { count: 'exact' });

      // Apply filters
      if (actionType && actionType !== 'all') {
        query = query?.eq('action_type', actionType);
      }

      if (entityType && entityType !== 'all') {
        query = query?.eq('entity_type', entityType);
      }

      if (startDate) {
        query = query?.gte('timestamp', startDate);
      }

      if (endDate) {
        query = query?.lte('timestamp', endDate);
      }

      if (searchTerm) {
        query = query?.or(`change_summary.ilike.%${searchTerm}%,entity_name.ilike.%${searchTerm}%,user_name.ilike.%${searchTerm}%`);
      }

      // Apply ordering and limit
      query = query?.order('timestamp', { ascending: false })?.limit(limit);

      const { data, error, count } = await query;
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getFiltered audit_logs:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return { data: [], count: 0 };
        } else {
          console.log('Data error in getFiltered audit_logs:', error?.message);
          return { data: [], count: 0 };
        }
      }
      
      return {
        data: convertKeysToCamelCase(data || []),
        count: count || 0
      };
    } catch (error) {
      console.error('Error fetching filtered audit logs:', error);
      throw error;
    }
  },

  async getByEntityId(entityId) {
    try {
      const { data, error } = await supabase?.from('audit_logs')?.select('*')?.eq('entity_id', entityId)?.order('timestamp', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getByEntityId audit_logs:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error in getByEntityId audit_logs:', error?.message);
          return [];
        }
      }
      
      return convertKeysToCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching audit logs by entity:', error);
      throw error;
    }
  },

  async getStatistics() {
    try {
      const { data, error } = await supabase?.from('audit_logs')?.select('action_type, entity_type');
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error in getStatistics audit_logs:', error?.message);
          throw error;
        } else {
          console.log('Data error in getStatistics audit_logs:', error?.message);
          return {
            totalLogs: 0,
            byAction: {},
            byEntity: {},
            recentActivity: 0
          };
        }
      }

      const logs = data || [];
      const byAction = {};
      const byEntity = {};

      logs?.forEach(log => {
        byAction[log?.action_type] = (byAction?.[log?.action_type] || 0) + 1;
        byEntity[log?.entity_type] = (byEntity?.[log?.entity_type] || 0) + 1;
      });

      return {
        totalLogs: logs?.length,
        byAction,
        byEntity,
        recentActivity: logs?.length
      };
    } catch (error) {
      console.error('Error fetching audit log statistics:', error);
      throw error;
    }
  },

  async exportToCSV(filters = {}) {
    try {
      const { data } = await this.getFiltered({ ...filters, limit: 10000 });
      
      if (!data || data?.length === 0) {
        return null;
      }

      // CSV headers
      const headers = [
        'Timestamp',
        'Action',
        'Entity Type',
        'Entity Name',
        'User',
        'Change Summary',
        'IP Address',
        'Session ID'
      ];

      // CSV rows
      const rows = data?.map(log => [
        new Date(log?.timestamp)?.toLocaleString(),
        log?.actionType,
        log?.entityType,
        log?.entityName || 'N/A',
        log?.userName || 'System',
        log?.changeSummary,
        log?.ipAddress || 'N/A',
        log?.sessionId || 'N/A'
      ]);

      // Combine headers and rows
      const csvContent = [
        headers?.join(','),
        ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
      ]?.join('\n');

      return csvContent;
    } catch (error) {
      console.error('Error exporting audit logs to CSV:', error);
      throw error;
    }
  }
};

// ============================================================================
// DASHBOARD SERVICE
// ============================================================================

export const dashboardService = {
  /**
   * Get comprehensive dashboard metrics
   * @param {Object} filters - { department: 'all' | departmentName, period: 'last7days' | 'last30days' | 'last90days' }
   */
  async getDashboardMetrics(filters = {}) {
    try {
      const { department = 'all', period = 'last30days' } = filters;
      
      // Calculate date range
      const periodDays = {
        'last7days': 7,
        'last30days': 30,
        'last90days': 90
      };
      const daysAgo = periodDays?.[period] || 30;
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);
      const startDateStr = startDate?.toISOString()?.split('T')?.[0];

      // Build employee query
      let employeeQuery = supabase?.from('employees')?.select('*');
      if (department !== 'all') {
        employeeQuery = employeeQuery?.eq('department', department);
      }
      const { data: employees, error: empError } = await employeeQuery;
      
      if (empError) throw empError;

      // Build violation query
      let violationQuery = supabase?.from('violation_records')?.select('*, employees!inner(department)')?.gte('date', startDateStr);
      
      if (department !== 'all') {
        violationQuery = violationQuery?.eq('employees.department', department);
      }
      
      const { data: violations, error: violError } = await violationQuery;
      if (violError) throw violError;

      // Calculate metrics
      const totalEmployees = employees?.length || 0;
      const activeEmployees = employees?.filter(e => e?.status === 'Active')?.length || 0;
      
      // Overall compliance score (average of all employee compliance scores)
      const avgComplianceScore = totalEmployees > 0
        ? employees?.reduce((sum, emp) => sum + (emp?.compliance_score || 100), 0) / totalEmployees
        : 0;

      // Total active violations (current points > 0)
      const totalActiveViolations = violations?.length || 0;

      // High-risk employees (current_points >= 10)
      const highRiskEmployees = employees?.filter(e => (e?.current_points || 0) >= 10)?.length || 0;

      // Compliance cost impact (sum of all violation points * $100 per point)
      const totalPoints = violations?.reduce((sum, v) => sum + (parseFloat(v?.points) || 0), 0) || 0;
      const costImpact = totalPoints * 100;

      return {
        overallComplianceScore: avgComplianceScore,
        totalActiveViolations,
        highRiskEmployees,
        complianceCostImpact: costImpact,
        totalEmployees,
        activeEmployees
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  },

  /**
   * Get violation trend data for chart
   */
  async getViolationTrends(filters = {}) {
    try {
      const { department = 'all', period = 'last30days' } = filters;
      
      const periodDays = {
        'last7days': 7,
        'last30days': 30,
        'last90days': 90
      };
      const daysAgo = periodDays?.[period] || 30;
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);
      const startDateStr = startDate?.toISOString()?.split('T')?.[0];

      let query = supabase?.from('violation_records')?.select('*, employees!inner(department)')?.gte('date', startDateStr)?.order('date', { ascending: true });
      
      if (department !== 'all') {
        query = query?.eq('employees.department', department);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Group by week
      const weeklyData = {};
      data?.forEach(violation => {
        const date = new Date(violation?.date);
        const weekStart = new Date(date);
        weekStart?.setDate(date?.getDate() - date?.getDay());
        const weekKey = weekStart?.toISOString()?.split('T')?.[0];
        
        if (!weeklyData?.[weekKey]) {
          weeklyData[weekKey] = { violations: 0, points: 0 };
        }
        weeklyData[weekKey].violations += 1;
        weeklyData[weekKey].points += parseFloat(violation?.points) || 0;
      });

      // Convert to array and format
      const trendData = Object.entries(weeklyData)?.sort(([a], [b]) => new Date(a) - new Date(b))?.map(([date, data], index) => ({
          period: `Week ${index + 1}`,
          violations: data?.violations,
          points: Math.round(data?.points)
        }));

      return trendData;
    } catch (error) {
      console.error('Error fetching violation trends:', error);
      throw error;
    }
  },

  /**
   * Get recent alerts based on thresholds
   */
  async getRecentAlerts(filters = {}) {
    try {
      const { department = 'all' } = filters;
      
      let query = supabase?.from('employees')?.select('*')?.order('current_points', { ascending: false });
      
      if (department !== 'all') {
        query = query?.eq('department', department);
      }

      const { data: employees, error } = await query;
      if (error) throw error;

      const alerts = [];
      
      employees?.forEach(emp => {
        const points = emp?.current_points || 0;
        
        if (points >= 20) {
          alerts?.push({
            id: `alert-${emp?.id}`,
            severity: 'critical',
            message: `Employee ${emp?.name} reached termination threshold with ${points} points accumulated`,
            department: emp?.department || 'Unknown',
            timestamp: new Date()
          });
        } else if (points >= 15) {
          alerts?.push({
            id: `alert-${emp?.id}`,
            severity: 'critical',
            message: `Employee ${emp?.name} exceeded 15-point threshold with ${points} accumulated points`,
            department: emp?.department || 'Unknown',
            timestamp: new Date()
          });
        } else if (points >= 10) {
          alerts?.push({
            id: `alert-${emp?.id}`,
            severity: 'high',
            message: `Employee ${emp?.name} approaching warning threshold with ${points} points`,
            department: emp?.department || 'Unknown',
            timestamp: new Date()
          });
        }
      });

      return alerts?.slice(0, 5); // Return top 5 alerts
    } catch (error) {
      console.error('Error fetching recent alerts:', error);
      throw error;
    }
  },

  /**
   * Get top violators list
   */
  async getTopViolators(filters = {}) {
    try {
      const { department = 'all', limit = 5 } = filters;
      
      let query = supabase?.from('employees')?.select('*')?.gt('current_points', 0)?.order('current_points', { ascending: false })?.limit(limit);
      
      if (department !== 'all') {
        query = query?.eq('department', department);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(emp => {
        const points = emp?.current_points || 0;
        let riskLevel = 'low';
        if (points >= 15) riskLevel = 'critical';
        else if (points >= 10) riskLevel = 'high';
        else if (points >= 5) riskLevel = 'medium';

        return {
          id: emp?.id,
          name: emp?.name,
          avatar: emp?.avatar || '',
          avatarAlt: emp?.avatar_alt || `Profile picture of ${emp?.name}`,
          department: emp?.department || 'Unknown',
          points: points,
          riskLevel
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching top violators:', error);
      throw error;
    }
  },

  /**
   * Get department comparison data
   */
  async getDepartmentComparison() {
    try {
      const { data: departments, error: deptError } = await supabase?.from('departments')?.select('name');
      
      if (deptError) throw deptError;

      const { data: employees, error: empError } = await supabase?.from('employees')?.select('department, compliance_score');
      
      if (empError) throw empError;

      // Group by department and calculate average score
      const deptScores = {};
      employees?.forEach(emp => {
        const dept = emp?.department || 'Unknown';
        if (!deptScores?.[dept]) {
          deptScores[dept] = { total: 0, count: 0 };
        }
        deptScores[dept].total += emp?.compliance_score || 100;
        deptScores[dept].count += 1;
      });

      const comparisonData = Object.entries(deptScores)?.map(([dept, data]) => ({
        department: dept,
        score: Math.round(data?.total / data?.count)
      }));

      return comparisonData;
    } catch (error) {
      console.error('Error fetching department comparison:', error);
      throw error;
    }
  }
};